import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { useTheme } from '../context/ThemeContext';
import { Button } from '../components/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { Sun, Moon, Globe, User, LogOut, Settings, Menu, X, CalendarSearch, Activity, Sparkles, CalendarDays, Flame, Users, Crown } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Navbar = () => {
    const { isAuthenticated, user, logout, token } = useAuth();
    const { language, toggleLanguage, t } = useLanguage();
    const { theme, toggleTheme } = useTheme();
    const location = useLocation();
    const navigate = useNavigate();
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const [isAdmin, setIsAdmin] = useState(false);
    const [hasProAccess, setHasProAccess] = useState(false);

    // Check if user is admin and has PRO access
    useEffect(() => {
        const checkAdmin = async () => {
            if (!token) {
                setIsAdmin(false);
                setHasProAccess(false);
                return;
            }
            try {
                const response = await fetch(`${API_URL}/api/auth/admin/check`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                });
                if (response.ok) {
                    const data = await response.json();
                    setIsAdmin(data.is_admin);
                    setHasProAccess(data.has_pro_access);
                }
            } catch (error) {
                console.error('Admin check error:', error);
                setIsAdmin(false);
                setHasProAccess(false);
            }
        };
        checkAdmin();
    }, [token]);

    const handleLogout = () => {
        logout();
        navigate('/');
    };

    const isActive = (path) => location.pathname === path;

    return (
        <nav className="sticky top-0 z-50 bg-background/80 backdrop-blur-xl border-b border-border" data-testid="navbar">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="flex items-center justify-between h-16">
                    {/* Logo */}
                    <Link to="/" className="flex items-center gap-2" data-testid="nav-logo">
                        <span className="font-serif text-xl font-bold">
                            <span className="font-chinese text-wealth">通书</span>
                            <span className="text-foreground"> Online Tong Shu</span>
                        </span>
                    </Link>

                    {/* Desktop Navigation */}
                    <div className="hidden md:flex items-center gap-6">
                        <Link 
                            to="/" 
                            className={`text-sm font-medium transition-colors ${isActive('/') ? 'text-foreground' : 'text-muted-foreground hover:text-foreground'}`}
                            data-testid="nav-home"
                        >
                            {t('home')}
                        </Link>
                        
                        <Link 
                            to="/energy-chart" 
                            className={`text-sm font-medium transition-colors ${location.pathname.startsWith('/energy-chart') ? 'text-foreground' : 'text-muted-foreground hover:text-foreground'}`}
                            data-testid="nav-energy-chart"
                        >
                            {language === 'zh' ? '能量圖' : 'Energy Chart'}
                        </Link>
                        
                        <Link 
                            to="/calendar" 
                            className={`text-sm font-medium transition-colors ${isActive('/calendar') ? 'text-foreground' : 'text-muted-foreground hover:text-foreground'}`}
                            data-testid="nav-calendar"
                        >
                            {t('dashboard')}
                        </Link>
                        
                        {isAuthenticated && (
                            <Link 
                                to="/personal-analysis" 
                                className={`text-sm font-medium transition-colors ${isActive('/personal-analysis') ? 'text-foreground' : 'text-muted-foreground hover:text-foreground'}`}
                                data-testid="nav-personal-analysis"
                            >
                                {language === 'zh' ? '個人分析' : 'Personal Date Analysis'}
                            </Link>
                        )}
                        
                        <Link 
                            to="/crimson-horse" 
                            className={`text-sm font-medium transition-colors ${isActive('/crimson-horse') ? 'text-foreground text-red-600' : 'text-red-500 hover:text-red-600'}`}
                            data-testid="nav-crimson-horse"
                        >
                            {language === 'zh' ? '🔥 赤馬' : '🔥 Crimson Horse'}
                        </Link>
                        
                        <Link 
                            to="/subscription" 
                            className={`text-sm font-medium transition-colors ${isActive('/subscription') ? 'text-foreground text-amber-600' : 'text-amber-500 hover:text-amber-600'}`}
                            data-testid="nav-pricing"
                        >
                            {language === 'zh' ? '定價' : 'Pricing'}
                        </Link>
                    </div>

                    {/* Right side actions */}
                    <div className="flex items-center gap-2">
                        {/* Language Toggle */}
                        <Button 
                            variant="ghost" 
                            size="sm" 
                            onClick={toggleLanguage}
                            className="hidden sm:flex items-center gap-1"
                            data-testid="language-toggle"
                        >
                            <Globe className="w-4 h-4" />
                            <span className="text-xs font-medium">{language.toUpperCase()}</span>
                        </Button>

                        {/* Theme Toggle */}
                        <Button 
                            variant="ghost" 
                            size="icon" 
                            onClick={toggleTheme}
                            data-testid="theme-toggle"
                        >
                            {theme === 'dark' ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
                        </Button>

                        {/* Auth Buttons / User Menu */}
                        {isAuthenticated ? (
                            <div className="flex items-center gap-2">
                                <Button 
                                    variant="ghost" 
                                    size="sm" 
                                    onClick={handleLogout}
                                    className="hidden sm:flex text-destructive hover:text-destructive"
                                    data-testid="logout-btn"
                                >
                                    <LogOut className="w-4 h-4 mr-1" />
                                    {t('logout')}
                                </Button>
                                <DropdownMenu>
                                    <DropdownMenuTrigger asChild>
                                        <Button variant="ghost" size="icon" className="rounded-full" data-testid="user-menu-btn">
                                            <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                                                <User className="w-4 h-4" />
                                            </div>
                                        </Button>
                                    </DropdownMenuTrigger>
                                    <DropdownMenuContent align="end" className="w-56">
                                        <div className="px-2 py-1.5">
                                            <p className="text-sm font-medium">{user?.name}</p>
                                            <p className="text-xs text-muted-foreground">{user?.email}</p>
                                        </div>
                                        <DropdownMenuSeparator />
                                        <DropdownMenuItem asChild>
                                            <Link to="/calendar" className="cursor-pointer" data-testid="menu-calendar">
                                                <CalendarDays className="w-4 h-4 mr-2" />
                                                {t('dashboard')}
                                            </Link>
                                        </DropdownMenuItem>
                                        <DropdownMenuItem asChild>
                                            <Link to="/energy-chart" className="cursor-pointer" data-testid="menu-energy-chart">
                                                <Activity className="w-4 h-4 mr-2" />
                                                {language === 'zh' ? '能量圖' : 'Energy Chart'}
                                            </Link>
                                        </DropdownMenuItem>
                                        <DropdownMenuItem asChild>
                                            <Link to="/personal-analysis" className="cursor-pointer" data-testid="menu-personal-analysis">
                                                <Sparkles className="w-4 h-4 mr-2" />
                                                {language === 'zh' ? '個人分析' : 'Personal Date Analysis'}
                                            </Link>
                                        </DropdownMenuItem>
                                        <DropdownMenuItem asChild>
                                            <Link to="/crimson-horse" className="cursor-pointer text-red-600" data-testid="menu-crimson-horse">
                                                <Flame className="w-4 h-4 mr-2" />
                                                {language === 'zh' ? '赤馬2026' : 'Crimson Horse 2026'}
                                            </Link>
                                        </DropdownMenuItem>
                                    <DropdownMenuSeparator />
                                    <DropdownMenuItem asChild>
                                        <Link to="/settings" className="cursor-pointer" data-testid="menu-settings">
                                            <Settings className="w-4 h-4 mr-2" />
                                            {language === 'zh' ? '個人設定' : 'Personal Settings'}
                                        </Link>
                                    </DropdownMenuItem>
                                    {isAdmin && (
                                        <DropdownMenuItem asChild>
                                            <Link to="/date-selection" className="cursor-pointer" data-testid="menu-date-selection">
                                                <CalendarSearch className="w-4 h-4 mr-2" />
                                                {language === 'zh' ? '個人擇日' : 'Date Selection'}
                                            </Link>
                                        </DropdownMenuItem>
                                    )}
                                    {!hasProAccess && (
                                        <DropdownMenuItem asChild>
                                            <Link to="/subscription" className="cursor-pointer text-amber-600 font-medium" data-testid="menu-upgrade">
                                                <Crown className="w-4 h-4 mr-2" />
                                                {language === 'zh' ? '升級至 PRO' : 'Upgrade to PRO'}
                                            </Link>
                                        </DropdownMenuItem>
                                    )}
                                    {isAdmin && (
                                        <DropdownMenuItem asChild>
                                            <Link to="/admin/dashboard" className="cursor-pointer text-purple-600" data-testid="menu-user-management">
                                                <Users className="w-4 h-4 mr-2" />
                                                {language === 'zh' ? '用戶管理' : 'User Management'}
                                            </Link>
                                        </DropdownMenuItem>
                                    )}
                                    <DropdownMenuItem 
                                        onClick={handleLogout} 
                                        className="cursor-pointer text-destructive"
                                        data-testid="menu-logout"
                                    >
                                        <LogOut className="w-4 h-4 mr-2" />
                                        {t('logout')}
                                    </DropdownMenuItem>
                                </DropdownMenuContent>
                            </DropdownMenu>
                            </div>
                        ) : (
                            <div className="hidden sm:flex items-center gap-2">
                                <Link to="/login">
                                    <Button variant="ghost" size="sm" data-testid="nav-login">
                                        {t('login')}
                                    </Button>
                                </Link>
                                <Link to="/register">
                                    <Button size="sm" className="rounded-full" data-testid="nav-register">
                                        {t('register')}
                                    </Button>
                                </Link>
                            </div>
                        )}

                        {/* Mobile Menu Button */}
                        <Button 
                            variant="ghost" 
                            size="icon" 
                            className="md:hidden"
                            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                            data-testid="mobile-menu-btn"
                        >
                            {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
                        </Button>
                    </div>
                </div>

                {/* Mobile Menu */}
                {mobileMenuOpen && (
                    <div className="md:hidden py-4 border-t border-border animate-fade-in" data-testid="mobile-menu">
                        <div className="flex flex-col gap-2">
                            <Link 
                                to="/" 
                                className="px-3 py-2 rounded-lg hover:bg-accent"
                                onClick={() => setMobileMenuOpen(false)}
                            >
                                {t('home')}
                            </Link>
                            
                            <Link 
                                to="/energy-chart" 
                                className="px-3 py-2 rounded-lg hover:bg-accent"
                                onClick={() => setMobileMenuOpen(false)}
                            >
                                {language === 'zh' ? '能量圖' : 'Energy Chart'}
                            </Link>
                            <Link 
                                to="/chinese-calendar" 
                                className="px-3 py-2 rounded-lg hover:bg-accent"
                                onClick={() => setMobileMenuOpen(false)}
                            >
                                {language === 'zh' ? '農曆' : 'Chinese Calendar'}
                            </Link>
                            <Link 
                                to="/crimson-horse" 
                                className="px-3 py-2 rounded-lg hover:bg-accent text-red-600"
                                onClick={() => setMobileMenuOpen(false)}
                            >
                                {language === 'zh' ? '🔥 赤馬2026' : '🔥 Crimson Horse 2026'}
                            </Link>
                            
                            <Link 
                                to="/calendar" 
                                className="px-3 py-2 rounded-lg hover:bg-accent"
                                onClick={() => setMobileMenuOpen(false)}
                            >
                                {t('dashboard')}
                            </Link>
                            
                            {isAuthenticated ? (
                                <>
                                    {isAdmin && (
                                        <Link 
                                            to="/date-selection" 
                                            className="px-3 py-2 rounded-lg hover:bg-accent"
                                            onClick={() => setMobileMenuOpen(false)}
                                        >
                                            {language === 'zh' ? '個人擇日' : 'Date Selection'}
                                        </Link>
                                    )}
                                    <Link 
                                        to="/personal-analysis" 
                                        className="px-3 py-2 rounded-lg hover:bg-accent"
                                        onClick={() => setMobileMenuOpen(false)}
                                    >
                                        {language === 'zh' ? '個人分析' : 'Personal Date Analysis'}
                                    </Link>
                                    <Link 
                                        to="/settings" 
                                        className="px-3 py-2 rounded-lg hover:bg-accent"
                                        onClick={() => setMobileMenuOpen(false)}
                                    >
                                        {language === 'zh' ? '個人設定' : 'Personal Settings'}
                                    </Link>
                                    {isAdmin && (
                                        <Link 
                                            to="/admin/dashboard" 
                                            className="px-3 py-2 rounded-lg hover:bg-accent text-purple-600"
                                            onClick={() => setMobileMenuOpen(false)}
                                        >
                                            {language === 'zh' ? '用戶管理' : 'User Management'}
                                        </Link>
                                    )}
                                    <button 
                                        onClick={() => { handleLogout(); setMobileMenuOpen(false); }}
                                        className="px-3 py-2 rounded-lg hover:bg-accent text-left text-destructive"
                                    >
                                        {t('logout')}
                                    </button>
                                </>
                            ) : (
                                <>
                                    <Link 
                                        to="/login" 
                                        className="px-3 py-2 rounded-lg hover:bg-accent"
                                        onClick={() => setMobileMenuOpen(false)}
                                    >
                                        {t('login')}
                                    </Link>
                                    <Link 
                                        to="/register" 
                                        className="px-3 py-2 rounded-lg hover:bg-accent"
                                        onClick={() => setMobileMenuOpen(false)}
                                    >
                                        {t('register')}
                                    </Link>
                                </>
                            )}
                            
                            <div className="flex items-center gap-2 px-3 py-2">
                                <Button variant="outline" size="sm" onClick={toggleLanguage}>
                                    <Globe className="w-4 h-4 mr-1" />
                                    {language.toUpperCase()}
                                </Button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </nav>
    );
};

export default Navbar;
